import { type Mode } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { isMentionableReferenceType } from '@apps/web/features/phoebe_chat_v3/model/mention_candidates';
import {
    type MentionToken,
    uniqueMentionLabel,
} from '@apps/web/features/phoebe_chat_v3/model/mentions';
import {
    isSourceCitationType,
    parseReferenceTokens,
    referenceFallbackLabel,
    type ReferenceSegment,
    type ReferenceType,
} from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';

export type MessageClipboardPayload = Readonly<{
    readonly text: string;
    readonly labels: Readonly<Record<string, string>>;
}>;

export type MessageClipboardScope = Readonly<{
    readonly organizationId: string;
    readonly mode: Mode;
}>;

type ReferenceLabelReader = (type: ReferenceType, id: string) => string | null;

let lastCopiedMessage:
    | (MessageClipboardScope &
          MessageClipboardPayload & { readonly plain: string })
    | null = null;

// The OS pasteboard can rewrite line endings, trailing newlines, and the
// narrow spaces in formatted times.
function comparableText(value: string): string {
    return value.replace(/\s+/gu, ' ').trim();
}

function referenceKey(type: ReferenceType, id: string): string {
    return `${type}:${id}`;
}

function readableReference(
    segment: Extract<ReferenceSegment, { readonly kind: 'reference' }>,
    label: string,
): string {
    return isSourceCitationType(segment.type)
        ? (segment.phrase ?? label)
        : label;
}

function forgetCopiedMessage(): void {
    lastCopiedMessage = null;
}

function readableMessage(
    text: string,
    labelFor: ReferenceLabelReader,
): Readonly<{
    readonly plain: string;
    readonly labels: Readonly<Record<string, string>>;
}> {
    const labels: Record<string, string> = {};
    let plain = '';
    for (const segment of parseReferenceTokens(text)) {
        if (segment.kind === 'text') {
            plain += segment.text;
            continue;
        }
        const label =
            labelFor(segment.type, segment.id) ??
            referenceFallbackLabel(segment.type);
        labels[referenceKey(segment.type, segment.id)] = label;
        plain += readableReference(segment, label);
    }
    return { plain, labels };
}

export function messagePlainText(
    text: string,
    labelFor: ReferenceLabelReader,
): string {
    return readableMessage(text, labelFor).plain;
}

export function rememberCopiedMessage(
    text: string,
    labelFor: ReferenceLabelReader,
    scope: MessageClipboardScope,
): string {
    const { plain, labels } = readableMessage(text, labelFor);
    lastCopiedMessage = {
        organizationId: scope.organizationId,
        mode: scope.mode,
        plain,
        text,
        labels,
    };
    // The copy button writes without a copy event; a native copy replaces it.
    document.addEventListener('copy', forgetCopiedMessage, { once: true });
    return plain;
}

export function recallCopiedMessage(
    plain: string,
    scope: MessageClipboardScope,
): MessageClipboardPayload | null {
    if (
        lastCopiedMessage === null ||
        lastCopiedMessage.organizationId !== scope.organizationId ||
        lastCopiedMessage.mode !== scope.mode ||
        comparableText(lastCopiedMessage.plain) !== comparableText(plain)
    ) {
        return null;
    }
    return { text: lastCopiedMessage.text, labels: lastCopiedMessage.labels };
}

export function messageClipboardToDraft(
    payload: MessageClipboardPayload,
    mentions: ReadonlyArray<MentionToken>,
): Readonly<{
    readonly text: string;
    readonly mentions: ReadonlyArray<MentionToken>;
}> {
    const added: Array<MentionToken> = [];
    let text = '';
    for (const segment of parseReferenceTokens(payload.text)) {
        if (segment.kind === 'text') {
            text += segment.text;
            continue;
        }
        const label =
            payload.labels[referenceKey(segment.type, segment.id)] ??
            referenceFallbackLabel(segment.type);
        if (!isMentionableReferenceType(segment.type)) {
            text += readableReference(segment, label);
            continue;
        }
        const token = `[[${segment.type}:${segment.id}]]`;
        const known = [...mentions, ...added];
        const existing = known.find((mention) => mention.token === token);
        if (existing !== undefined) {
            text += `@${existing.label}`;
            continue;
        }
        const mention: MentionToken = {
            label: uniqueMentionLabel(label, null, known, token),
            token,
            type: segment.type,
        };
        added.push(mention);
        text += `@${mention.label}`;
    }
    return { text, mentions: added };
}
